import { useState, useEffect, useCallback } from "react";
import { X, FlaskConical, Lock, RotateCcw, Copy, ClipboardPaste, ChevronRight, Loader2, CircleDot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, LLM_MODELS, type LLMModelId, type CustomPrompts, type DefaultPromptsResponse, type VendorEvaluation } from "@/services/api";

interface CustomPromptsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (model: LLMModelId, customPrompts?: CustomPrompts) => void;
  vendorId?: string;
  previousEvaluations?: VendorEvaluation[];
}

type PromptSection =
  | "system"
  | "SEE"
  | "CHANGE"
  | "ADAPT"
  | "USE"
  | "LEARN"
  | "EXIT"
  | "executive_summary"
  | "key_questions";

const SECTION_LABELS: Record<PromptSection, string> = {
  system: "System Prompt",
  SEE: "SEE - Transparency",
  CHANGE: "CHANGE - Customizability",
  ADAPT: "ADAPT - Vendor Agility",
  USE: "USE - Genuine Utility",
  LEARN: "LEARN - Transferable Knowledge",
  EXIT: "EXIT - Portability",
  executive_summary: "Executive Summary",
  key_questions: "Key Questions",
};

const ALL_SECTIONS: PromptSection[] = [
  "system",
  "SEE",
  "CHANGE",
  "ADAPT",
  "USE",
  "LEARN",
  "EXIT",
  "executive_summary",
  "key_questions",
];

export default function CustomPromptsModal({
  isOpen,
  onClose,
  onConfirm,
  previousEvaluations,
}: CustomPromptsModalProps) {
  const [selectedModel, setSelectedModel] = useState<LLMModelId>("claude-opus-4-7");
  const [activeSection, setActiveSection] = useState<PromptSection>("system");
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [defaultPrompts, setDefaultPrompts] = useState<DefaultPromptsResponse["prompts"] | null>(null);

  // Editable text state keyed by section
  const [editedPrompts, setEditedPrompts] = useState<Record<string, string>>({});

  // Load default prompts on open
  useEffect(() => {
    if (isOpen && !defaultPrompts) {
      setLoading(true);
      api.getDefaultPrompts()
        .then((res) => {
          setDefaultPrompts(res.prompts);
          // Initialize with defaults
          const initial: Record<string, string> = {
            system: res.prompts.system_prompt.editable,
            executive_summary: res.prompts.executive_summary_prompt.editable,
            key_questions: res.prompts.key_questions_prompt.editable,
          };
          for (const [id, cp] of Object.entries(res.prompts.criterion_prompts)) {
            initial[id] = cp.editable;
          }
          setEditedPrompts(initial);
        })
        .catch((err) => console.error("Failed to load default prompts:", err))
        .finally(() => setLoading(false));
    }
  }, [isOpen, defaultPrompts]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setDisclaimerAccepted(false);
      setActiveSection("system");
    }
  }, [isOpen]);

  const getDefaultForSection = useCallback(
    (section: PromptSection): string => {
      if (!defaultPrompts) return "";
      if (section === "system") return defaultPrompts.system_prompt.editable;
      if (section === "executive_summary") return defaultPrompts.executive_summary_prompt.editable;
      if (section === "key_questions") return defaultPrompts.key_questions_prompt.editable;
      return defaultPrompts.criterion_prompts[section]?.editable ?? "";
    },
    [defaultPrompts]
  );

  const getLockedForSection = useCallback(
    (section: PromptSection): string | null => {
      if (!defaultPrompts) return null;
      if (section === "system") return defaultPrompts.system_prompt.locked;
      if (section === "executive_summary" || section === "key_questions") return null;
      return defaultPrompts.criterion_prompts[section]?.locked ?? null;
    },
    [defaultPrompts]
  );

  const isModified = useCallback(
    (section: PromptSection): boolean => {
      const defaultText = getDefaultForSection(section);
      return (editedPrompts[section] ?? "") !== defaultText;
    },
    [editedPrompts, getDefaultForSection]
  );

  const hasAnyModification = ALL_SECTIONS.some((s) => isModified(s));

  const handleReset = (section: PromptSection) => {
    setEditedPrompts((prev) => ({
      ...prev,
      [section]: getDefaultForSection(section),
    }));
  };

  const handleRun = () => {
    if (!defaultPrompts) return;

    // Build custom_prompts with only changed prompts
    const customPrompts: CustomPrompts = {};
    let hasChanges = false;

    if (isModified("system")) {
      customPrompts.system_prompt = editedPrompts.system;
      hasChanges = true;
    }
    if (isModified("executive_summary")) {
      customPrompts.executive_summary_prompt = editedPrompts.executive_summary;
      hasChanges = true;
    }
    if (isModified("key_questions")) {
      customPrompts.key_questions_prompt = editedPrompts.key_questions;
      hasChanges = true;
    }

    const criterionIds = ["SEE", "CHANGE", "ADAPT", "USE", "LEARN", "EXIT"] as const;
    const modifiedCriteria: Record<string, string> = {};
    for (const id of criterionIds) {
      if (isModified(id)) {
        modifiedCriteria[id] = editedPrompts[id];
        hasChanges = true;
      }
    }
    if (Object.keys(modifiedCriteria).length > 0) {
      customPrompts.criterion_prompts = modifiedCriteria;
    }

    onConfirm(selectedModel, hasChanges ? customPrompts : undefined);
  };

  const handleCopyJson = () => {
    const customPrompts: CustomPrompts = {};
    if (isModified("system")) customPrompts.system_prompt = editedPrompts.system;
    if (isModified("executive_summary")) customPrompts.executive_summary_prompt = editedPrompts.executive_summary;
    if (isModified("key_questions")) customPrompts.key_questions_prompt = editedPrompts.key_questions;
    const criterionIds = ["SEE", "CHANGE", "ADAPT", "USE", "LEARN", "EXIT"] as const;
    const modifiedCriteria: Record<string, string> = {};
    for (const id of criterionIds) {
      if (isModified(id)) modifiedCriteria[id] = editedPrompts[id];
    }
    if (Object.keys(modifiedCriteria).length > 0) customPrompts.criterion_prompts = modifiedCriteria;
    navigator.clipboard.writeText(JSON.stringify(customPrompts, null, 2));
  };

  const handleImportJson = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = JSON.parse(text) as CustomPrompts;
      setEditedPrompts((prev) => {
        const next = { ...prev };
        if (parsed.system_prompt) next.system = parsed.system_prompt;
        if (parsed.executive_summary_prompt) next.executive_summary = parsed.executive_summary_prompt;
        if (parsed.key_questions_prompt) next.key_questions = parsed.key_questions_prompt;
        if (parsed.criterion_prompts) {
          for (const [id, text] of Object.entries(parsed.criterion_prompts)) {
            if (text) next[id] = text;
          }
        }
        return next;
      });
    } catch {
      console.error("Failed to import JSON from clipboard");
    }
  };

  const handleLoadPrevious = (evaluation: VendorEvaluation) => {
    if (!evaluation.custom_prompts) return;
    const cp = evaluation.custom_prompts;
    setEditedPrompts((prev) => {
      const next = { ...prev };
      if (cp.system_prompt) next.system = cp.system_prompt;
      if (cp.executive_summary_prompt) next.executive_summary = cp.executive_summary_prompt;
      if (cp.key_questions_prompt) next.key_questions = cp.key_questions_prompt;
      if (cp.criterion_prompts) {
        for (const [id, text] of Object.entries(cp.criterion_prompts)) {
          if (text) next[id] = text;
        }
      }
      return next;
    });
  };

  const previousWithCustomPrompts = previousEvaluations?.filter(
    (e) => e.custom_prompts_used && e.custom_prompts
  );

  if (!isOpen) return null;

  const locked = getLockedForSection(activeSection);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-4xl mx-4 overflow-hidden flex flex-col" style={{ maxHeight: "90vh" }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#063F6B] to-[#124C77] p-5 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-amber-300" />
              <h2 className="text-lg font-semibold text-white">Rerun with Custom Prompts</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10 min-w-[40px] min-h-[40px] flex items-center justify-center"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        {!disclaimerAccepted && (
          <div className="border-b border-border bg-slate-50 dark:bg-slate-800 p-4 shrink-0">
            <p className="text-sm text-slate-700 dark:text-slate-200 mb-3">
              This is an <strong>experimental feature</strong>. Customizing prompts may produce unexpected results.
              Locked sections ensure output format stays valid, but edited instructions may affect evaluation quality.
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={disclaimerAccepted}
                onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                className="rounded border-slate-400"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                I understand and want to proceed
              </span>
            </label>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading prompts...</span>
          </div>
        ) : (
          <>
            {/* Main content: sidebar + editor */}
            <div className="flex flex-1 min-h-0 overflow-hidden">
              {/* Sidebar */}
              <div className="w-52 border-r border-border bg-muted/30 overflow-y-auto shrink-0">
                <nav className="p-2 space-y-0.5">
                  {ALL_SECTIONS.map((section) => (
                    <button
                      key={section}
                      onClick={() => setActiveSection(section)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                        activeSection === section
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      <ChevronRight
                        className={`h-3 w-3 shrink-0 transition-transform ${
                          activeSection === section ? "rotate-90" : ""
                        }`}
                      />
                      <span className="truncate">{SECTION_LABELS[section]}</span>
                      {isModified(section) && (
                        <CircleDot className="h-3 w-3 shrink-0 text-amber-500" />
                      )}
                    </button>
                  ))}
                </nav>

                {/* Load from Previous */}
                {previousWithCustomPrompts && previousWithCustomPrompts.length > 0 && (
                  <div className="p-2 border-t border-border">
                    <p className="text-xs text-muted-foreground px-3 py-1 font-medium">Load Previous</p>
                    {previousWithCustomPrompts.map((ev) => (
                      <button
                        key={ev.id}
                        onClick={() => handleLoadPrevious(ev)}
                        disabled={!disclaimerAccepted}
                        className="w-full text-left px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-40"
                      >
                        {new Date(ev.created_at).toLocaleDateString()}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Editor Panel */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">
                    {SECTION_LABELS[activeSection]}
                  </h3>
                  {isModified(activeSection) && (
                    <button
                      onClick={() => handleReset(activeSection)}
                      disabled={!disclaimerAccepted}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Reset to Default
                    </button>
                  )}
                </div>

                {/* Editable textarea */}
                <textarea
                  value={editedPrompts[activeSection] ?? ""}
                  onChange={(e) =>
                    setEditedPrompts((prev) => ({
                      ...prev,
                      [activeSection]: e.target.value,
                    }))
                  }
                  disabled={!disclaimerAccepted}
                  rows={12}
                  className="w-full rounded-lg border border-border bg-background p-3 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter custom prompt instructions..."
                />

                {/* Locked zone */}
                {locked && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Lock className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">
                        Locked (Output Format) — always appended automatically
                      </span>
                    </div>
                    <div className="rounded-lg border border-border bg-muted/50 p-3 text-xs font-mono text-muted-foreground whitespace-pre-wrap select-text">
                      {locked}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-border p-4 shrink-0 space-y-3">
              {/* Model selector */}
              <div className="flex flex-wrap gap-2">
                {LLM_MODELS.map((model) => (
                  <label
                    key={model.id}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-sm transition-all ${
                      selectedModel === model.id
                        ? "border-primary bg-primary/5 text-foreground"
                        : "border-border text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    <input
                      type="radio"
                      name="cp-model"
                      value={model.id}
                      checked={selectedModel === model.id}
                      onChange={() => setSelectedModel(model.id)}
                      className="sr-only"
                    />
                    <div
                      className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${
                        selectedModel === model.id ? "border-primary" : "border-muted-foreground/40"
                      }`}
                    >
                      {selectedModel === model.id && (
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      )}
                    </div>
                    {model.name}
                  </label>
                ))}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleImportJson}
                  disabled={!disclaimerAccepted}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors disabled:opacity-40"
                  title="Import prompts from clipboard JSON"
                >
                  <ClipboardPaste className="h-3.5 w-3.5" />
                  Import JSON
                </button>
                <button
                  onClick={handleCopyJson}
                  disabled={!hasAnyModification}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors disabled:opacity-40"
                  title="Copy modified prompts as JSON"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy JSON
                </button>
                <div className="flex-1" />
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="brand"
                  onClick={handleRun}
                  disabled={!disclaimerAccepted}
                >
                  <FlaskConical className="h-4 w-4 mr-1.5" />
                  {hasAnyModification ? "Run with Custom Prompts" : "Run Evaluation"}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
